import React from 'react';
import bemBlock from '@gik/core/utils/bemBlock';
import {
  useTable,
  Cell,
  useFlexLayout,
  useAbsoluteLayout,
  useBlockLayout,
  useGlobalFilter,
  useFilters,
  useSortBy,
  usePagination,
  useExpanded,
  TableState,
  Column,
  PluginHook,
  TableInstance,
  UsePaginationInstanceProps,
  UseGlobalFiltersInstanceProps,
  UseGlobalFiltersState,
  UseExpandedHooks,
  Row,
  UseExpandedRowProps,
  UsePaginationState,
  UseSortByColumnOptions,
  UseTableColumnProps,
  UseSortByHooks,
  UseSortByColumnProps,
  UseFiltersColumnProps,
} from 'react-table';
import { UITableVariant } from './types';
import { Breakpoint, useBreakpoint } from '@gik/core/hooks/CustomHooks';
import { DataCards } from './DataCards';
import { useSticky } from 'react-table-sticky';
import { Scrollbars } from 'react-custom-scrollbars';
import { DefaultColumnFilter, fuzzyTextFilterFn, GlobalFilter } from './TableFilters';
import SvgIcon from '../SvgIcon';
import { TablePagination } from './TablePagination';
import { AnimatePresence } from 'framer-motion';
import { AnimateHeight } from './AnimateHeight';

export type ColumnWithPlugins<T extends Object> = Column<T> &
  UseSortByColumnOptions<T> & {
    Header: ((expandedProps: UseExpandedHooks<T>) => React.ReactNode) | string;
  } & {
    variant?: UITableVariant;
    style?: React.CSSProperties;
  };

export type ColumnWithAllPlugins<T extends object> = ColumnWithPlugins<T> &
  UseTableColumnProps<T> &
  UseSortByHooks<T> &
  UseSortByColumnProps<T> &
  UseFiltersColumnProps<T>;

export type TableInstanceWithPlugins<T extends Object> = TableInstance<T> &
  UsePaginationInstanceProps<T> &
  UseGlobalFiltersInstanceProps<T> & {
    state: UseGlobalFiltersState<T> & UsePaginationState<T>;
  };
export type RowWithPlugins<T extends Object> = Row<T> & UseExpandedRowProps<T>;
export interface ITableProps<T extends Object = {}> extends React.HTMLAttributes<HTMLTableElement> {
  data: T[];
  // TODO: fix typings, remove eslint-disable-next-line
  // eslint-disable-next-line
  columns?: ColumnWithPlugins<T>[];
  fullWidth?: boolean;
  scrollable?: boolean;
  hasStickyHeader?: boolean;
  hasFlexLayout?: boolean;
  hasAbsoluteLayout?: boolean;
  hasFixedLayout?: boolean;
  hasBlockLayout?: boolean;
  hasMobileCards?: boolean;
  hasGlobalFilter?: boolean;
  hasFilters?: boolean;
  hasSortBy?: boolean;
  hasExpandableRows?: boolean;
  hasCustomScrollbars?: boolean;
  hasPagination?: boolean;
  truncate?: boolean;
  perPage?: number;
  customToolbar?(props: TableInstanceWithPlugins<T>): React.ReactNode;
  customDetailsDisplay?(props: TableInstanceWithPlugins<T>): React.ReactElement;
  customEmptyFilterView?(props: TableInstanceWithPlugins<T>): React.ReactElement;
  customEmptyView?(props: TableInstanceWithPlugins<T>): React.ReactElement;
  initialPageIndex?: number;
  renderRowSubComponent?(props: { row: RowWithPlugins<T> }): React.ReactNode;
  caption?: React.ReactNode;
  variant?: UITableVariant;
  globalFilterPlaceholder?: string;
  globalFilterCollapse?: boolean;
  bodyMaxHeight?: string;
}

export function Table<T extends object>({
  data,
  columns,
  className,
  fullWidth,
  scrollable,
  hasStickyHeader,
  hasFlexLayout,
  hasFixedLayout,
  hasAbsoluteLayout,
  hasBlockLayout,
  hasMobileCards,
  hasGlobalFilter,
  hasFilters,
  hasSortBy,
  hasExpandableRows,
  hasPagination,
  hasCustomScrollbars,
  globalFilterPlaceholder = 'Search...',
  globalFilterCollapse,
  caption,
  variant,
  truncate,
  customToolbar,
  customDetailsDisplay,
  customEmptyFilterView,
  customEmptyView,
  perPage = 10,
  initialPageIndex = 0,
  renderRowSubComponent,
  bodyMaxHeight,
  ...otherProps
}: ITableProps<T>): React.ReactElement {
  const bem = bemBlock('table');

  const isMdUp = useBreakpoint(Breakpoint.MD);

  const plugins: PluginHook<T>[] = [];

  if (hasFlexLayout) plugins.push(useFlexLayout);
  if (hasAbsoluteLayout) plugins.push(useAbsoluteLayout);
  if (hasBlockLayout) plugins.push(useBlockLayout);
  if (hasStickyHeader) plugins.push(useSticky);
  if (hasFilters) plugins.push(useFilters);
  if (hasGlobalFilter) plugins.push(useGlobalFilter);
  if (hasSortBy) plugins.push(useSortBy);
  if (hasExpandableRows) plugins.push(useExpanded);
  if (hasPagination) plugins.push(usePagination);

  const filterTypes = React.useMemo(
    () => ({
      // Add a new fuzzyTextFilterFn filter type.
      fuzzyText: fuzzyTextFilterFn,
      // Or, override the default text filter to use
      // "startWith"
      text: (rows, id, filterValue) => {
        return rows.filter(row => {
          const rowValue = row.values[id];
          return rowValue !== undefined
            ? String(rowValue).toLowerCase().startsWith(String(filterValue).toLowerCase())
            : true;
        });
      },
    }),
    []
  );

  const defaultColumn = React.useMemo(
    () => ({
      // Let's set up our default Filter UI
      Filter: DefaultColumnFilter,
    }),
    []
  );

  const tableProps = useTable<T>(
    {
      ...otherProps,
      columns,
      data,
      // @ts-ignore
      defaultColumn,
      filterTypes,
      initialState: { pageIndex: initialPageIndex, pageSize: perPage } as Partial<TableState<T>>,
    },
    ...plugins
  ) as TableInstanceWithPlugins<T>;

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    page,
    prepareRow,
    state,
    visibleColumns,
    setGlobalFilter,
  } = tableProps;

  if (hasMobileCards && !isMdUp) {
    return <DataCards data={data} columns={columns} />;
  }

  const bodyStyle: React.CSSProperties = {};
  if (bodyMaxHeight) {
    bodyStyle.height = bodyMaxHeight;
  }

  const variants = {
    open: {
      opacity: 1,
      height: 'auto',
    },
    collapsed: { opacity: 0, height: '1px' },
  };

  const rowSet = (page || rows) as RowWithPlugins<T>[];

  function renderRows() {
    let _rows;

    if (!rowSet.length) {
      _rows = state.globalFilter ? (
        <div className={bem('empty-filter')}>
          {customEmptyFilterView ? (
            customEmptyFilterView(tableProps)
          ) : (
            <span>No results for &quot;{state.globalFilter}&quot;</span>
          )}
        </div>
      ) : (
        <div className={bem('empty')}>{customEmptyView ? customEmptyView(tableProps) : <span>No items</span>}</div>
      );
      return _rows;
    } else {
      _rows = rowSet.map((row, rowIndex) => {
        prepareRow(row);
        return [
          <tr className={bem('tr')} key={rowIndex} {...row.getRowProps()}>
            {row.cells.map((cell: Cell<T>, cellIndex: number) => {
              const colProps = cell.getCellProps();
              const column = columns[cellIndex];
              const colClasses = bem('td', [column?.accessor || column?.id, column?.variant]);
              if (colProps.style) delete colProps.style.zIndex;
              const colStyle = {
                ...column?.style,
                ...colProps.style,
              };
              return (
                <td {...colProps} style={colStyle} className={colClasses} key={`${rowIndex}-${cellIndex}`}>
                  <div className={bem('td-inner')}>{cell.render('Cell')}</div>
                </td>
              );
            })}
          </tr>,
          <AnimatePresence key={`${rowIndex}-expandable`}>
            {row.isExpanded && (
              <tr key={rowIndex + '-expandable'}>
                <td className={bem('td-expandable')} colSpan={visibleColumns.length}>
                  <AnimateHeight variants={variants} isVisible={row.isExpanded}>
                    {renderRowSubComponent({ row })}
                  </AnimateHeight>
                </td>
              </tr>
            )}
          </AnimatePresence>,
        ];
      });
    }

    return (
      <tbody className={bem('tbody')} {...getTableBodyProps()} style={!hasCustomScrollbars ? bodyStyle : null}>
        {_rows}
      </tbody>
    );
  }

  const body = renderRows();

  return (
    <div
      {...otherProps}
      className={bem(
        null,
        [
          { scrollable },
          { 'full-width': fullWidth },
          { [variant]: variant },
          { sticky: hasStickyHeader },
          { truncate },
          { 'sticky-header': hasStickyHeader },
          { 'flex-layout': hasFlexLayout },
          { 'auto-layout': !hasFixedLayout && !hasBlockLayout && !hasAbsoluteLayout && !hasFlexLayout },
          { 'fixed-layout': hasFixedLayout },
          { 'custom-scrollbars': hasCustomScrollbars },
          { empty: !rowSet.length },
        ],
        [className]
      )}
    >
      <table {...getTableProps()} className={bem('table')}>
        {caption && <caption>{caption}</caption>}
        {headerGroups && (
          <thead className={bem('thead')}>
            {hasGlobalFilter && (
              <tr className={bem('global-filter')}>
                <th colSpan={visibleColumns.length}>
                  <div className={bem('custom')}>{customToolbar && customToolbar(tableProps)}</div>
                  <GlobalFilter
                    collapse={globalFilterCollapse}
                    placeholder={globalFilterPlaceholder}
                    globalFilter={state.globalFilter}
                    setGlobalFilter={setGlobalFilter}
                  />
                </th>
              </tr>
            )}

            {headerGroups.map((headerGroup, index: number) => {
              const rowProps = headerGroup.getHeaderGroupProps();
              return (
                <tr className={bem('tr')} {...rowProps} key={index}>
                  {/* TODO: headerGroup.headers typings does not match documentation at https://react-table.tanstack.com/docs/api/useTable#headergroup-properties */}
                  {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                  {((headerGroup.headers as any) as ColumnWithAllPlugins<T>[]).map((column, colIndex: number) => {
                    const colProps = column.getHeaderProps(hasSortBy ? column.getSortByToggleProps() : undefined);
                    const colClasses = bem('th', [column?.accessor || column?.id, column?.variant]);

                    const colStyle = {
                      ...column?.style,
                      ...colProps.style,
                    };
                    return (
                      <th style={colStyle} className={colClasses} key={`${index}-${colIndex}`} {...colProps}>
                        <div className={bem('th-inner')}>
                          <div className={bem('th-label')}>{column.render('Header')}</div>
                          {hasSortBy && (
                            <div className={bem('th-sort')}>
                              {' '}
                              {column.isSorted ? (
                                column.isSortedDesc ? (
                                  <SvgIcon name="SortAscending" />
                                ) : (
                                  <SvgIcon name="SortDescending" />
                                )
                              ) : (
                                <SvgIcon className={bem('th-unsorted')} name="SortAscending" />
                              )}{' '}
                            </div>
                          )}
                        </div>

                        {hasFilters && (
                          <div
                            className={bem('th-filter')}
                            onClick={ev => {
                              ev.stopPropagation();
                            }}
                          >
                            {column.canFilter ? column.render('Filter') : null}
                          </div>
                        )}
                      </th>
                    );
                  })}
                </tr>
              );
            })}
          </thead>
        )}

        {hasCustomScrollbars && rowSet.length > 0 && <Scrollbars style={bodyStyle}>{body}</Scrollbars>}
        {!hasCustomScrollbars && rowSet.length > 0 && body}

        {hasPagination && (
          <tfoot>
            <tr>
              <td colSpan={visibleColumns.length}>
                <TablePagination<T> tableProps={tableProps} customDetailsDisplay={customDetailsDisplay} />
              </td>
            </tr>
          </tfoot>
        )}
      </table>

      {rowSet.length === 0 && body}
    </div>
  );
}
